/**
 * BuscadorGlobal — paleta de búsqueda (Ctrl+K / Cmd+K).
 *
 * Busca en tres fuentes:
 *  - Pantallas del menú visibles para el rol, por nombre, módulo o código
 *    ARES del escritorio ("4010" lleva a Pagos, "2050" a Refinanciamientos).
 *  - Clientes (si el rol tiene el programa "expediente"): por nombre, cédula,
 *    chasis o número de préstamo vía /api/buscarclientes, con debounce;
 *    seleccionar uno abre su expediente.
 *  - Chasis del inventario (misma llamada; se muestran si el rol además tiene
 *    "historialchasis"): por chasis o placa aunque la unidad no tenga
 *    préstamo; seleccionar uno abre el historial del chasis.
 *
 * Renderiza el botón disparador para el sidebar y el diálogo de búsqueda.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import InputBase from "@mui/material/InputBase";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import TwoWheelerRoundedIcon from "@mui/icons-material/TwoWheelerRounded";
import { useMaterialUIController } from "context";
import { useApi } from "lib/api";
import { estadoPrestamo } from "lib/prestamos";
import { tienePermisosModulo } from "lib/seguridad/permisosPorModulo";
import { tieneAccesoEntrada } from "modules/menu/menuVisible";

const normalizar = (txt) =>
  String(txt || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

/**
 * Acciones dentro de pantallas (tarjetas/di\u00e1logos) buscables por nombre.
 * La ruta lleva ?accion=... y la pantalla destino abre el di\u00e1logo sola.
 * Cada una se muestra solo si el rol tiene su programa ARES.
 */
const accion = (nombre, grupo, ruta, programa) => ({
  nombre,
  grupo,
  ruta,
  programa,
  codigos: /^ares\d+/.test(programa) ? [programa] : [],
});

const ACCIONES = [
  accion("Venta a cr\u00e9dito", "Ventas", "/facturacion/ventas?accion=credito", "ares2010"),
  accion("Venta al contado", "Ventas", "/facturacion/ventas?accion=contado", "ares2015"),
  accion("Cobrar inicial", "Iniciales", "/facturacion/iniciales?accion=cobrar", "ares4020"),
  accion("Conteo de efectivo", "Cuadre de Caja", "/facturacion/cuadres?accion=conteo", "cuadrecaja"),
  accion("Resumen por sucursal", "Cuadre de Caja", "/facturacion/cuadres?accion=resumen", "ares7075"),
  accion("Refinanciar pr\u00e9stamo", "Refinanciamientos", "/prestamos/refinanciamientos?accion=interno", "ares2050"),
  accion("Refinanciamiento externo", "Refinanciamientos", "/prestamos/refinanciamientos?accion=externo", "ares2060"),
  accion("Cobrar pago", "Pagos", "/pagos/gestion?accion=cobrar", "ares4010"),
  accion("Recibos por pr\u00e9stamo", "Pagos", "/pagos/gestion?accion=recibos", "ares4030"),
  accion("Cancelar pr\u00e9stamo", "Pr\u00e9stamos", "/prestamos/gestion?accion=cancelar", "cancelacionprestamos"),
  accion("Balance de pr\u00e9stamos", "Pr\u00e9stamos", "/prestamos/gestion?accion=balance", "ares3040"),
  accion("Pr\u00e9stamos saldados", "Pr\u00e9stamos", "/prestamos/gestion?accion=saldados", "ares3050"),
  accion("Cancelados por fechas", "Pr\u00e9stamos", "/prestamos/gestion?accion=cancelados", "listarcanceladosporfechas"),
  accion("Incobrables por fechas", "Pr\u00e9stamos", "/prestamos/gestion?accion=incobrables", "listarcanceladosporfechas"),
  accion("Beneficio recuperado", "Pr\u00e9stamos", "/prestamos/gestion?accion=beneficio", "beneficiorecuperado"),
  accion("Registrar incauto", "Incautos", "/incautos/gestion?accion=incauto", "ares5010"),
  accion("Registrar cargo", "Incautos", "/incautos/gestion?accion=cargo", "ares5060"),
  accion("Modificar valor incauto", "Incautos", "/incautos/gestion?accion=modificar", "ares8070"),
  accion("Reporte de cargos", "Incautos", "/incautos/gestion?accion=reporte", "ares5095"),
  accion("Pagar incautadores", "Incautos", "/incautos/pagos?accion=pagar", "ares5085"),
  accion("Cobrar matr\u00edcula", "Matr\u00edculas", "/matriculas/cobros?accion=cobrar", "ares6010"),
  accion("Informe de matr\u00edculas cobradas", "Matr\u00edculas", "/matriculas/cobros?accion=informe", "ares6020"),
  accion("Traspasar matr\u00edcula", "Traspasos", "/matriculas/traspasos?accion=traspasar", "ares6030"),
  accion("Informe de traspasos", "Traspasos", "/matriculas/traspasos?accion=informe", "traspaso"),
  accion("Reporte de consignaciones", "Consignaciones", "/inventario/consignaciones?accion=reporte", "consignaciones"),
  accion("Estado de cuenta del consignante", "Consignaciones", "/inventario/consignaciones?accion=estadocuenta", "consignaciones"),
  accion("Recepci\u00f3n de motor", "Taller", "/taller/ordenes?accion=recepcion", "ares9010"),
  accion("Salida de motor", "Taller", "/taller/ordenes?accion=salida", "ares9020"),
  accion("Reporte de gastos de taller", "Taller", "/taller/ordenes?accion=reporte", "ares9025"),
  accion("Registrar venta general", "Ventas Generales", "/ventasgenerales/ventas?accion=registrar", "ares9610"),
  accion("Reportes de ventas generales", "Ventas Generales", "/ventasgenerales/ventas?accion=reportes", "ares9630"),
  accion("Cobrar venta general", "Ventas Generales", "/ventasgenerales/pagos?accion=cobrar", "ares9650"),
  accion("Facturar x mayor", "Ventas x Mayor", "/ventasxmayor/facturas?accion=facturar", "ares9710"),
  accion("Modificar precio motor", "Ventas x Mayor", "/ventasxmayor/facturas?accion=precios", "ares9756"),
  accion("Chasis vendidos x mayor", "Ventas x Mayor", "/ventasxmayor/facturas?accion=chasis", "ares9755"),
  accion(
    "Entregar en consignación x mayor",
    "Ventas x Mayor",
    "/ventasxmayor/consignaciones?accion=conduce",
    "conducesxmayor"
  ),
  accion("Cobrar x mayor", "Ventas x Mayor", "/ventasxmayor/cobros?accion=cobrar", "ares9715"),
  accion("Informe de cobros x mayor", "Ventas x Mayor", "/ventasxmayor/cobros?accion=informe", "ares9750"),
  accion("Informe de ingresos x mayor", "Ventas x Mayor", "/ventasxmayor/cobros?accion=ingresos", "ares9750"),
  accion("Estado de cuenta x mayor", "Ventas x Mayor", "/ventasxmayor/clientes?accion=estado", "ares9730"),
  accion("Antig\u00fcedad de saldo x mayor", "Ventas x Mayor", "/ventasxmayor/clientes?accion=antiguedad", "ares9725"),
  accion("Completivos del inicial x mayor", "Ventas x Mayor", "/ventasxmayor/clientes?accion=completivos", "ares9725"),
  accion("Registrar factura de compra", "Cuentas x Pagar", "/cuentasxpagar/facturas?accion=registrar", "ares8510"),
  accion("Chasis de factura", "Cuentas x Pagar", "/cuentasxpagar/facturas?accion=chasis", "ares8550"),
  accion("Pagar a suplidores", "Cuentas x Pagar", "/cuentasxpagar/pagos?accion=pagar", "ares8515"),
  accion("Estado de cuenta suplidor", "Cuentas x Pagar", "/cuentasxpagar/suplidores?accion=estado", "ares8535"),
  accion("Antig\u00fcedad de saldo suplidores", "Cuentas x Pagar", "/cuentasxpagar/suplidores?accion=antiguedad", "ares8530"),
  accion("Agregar gasto", "Gastos", "/gastos/informegastos?accion=agregar", "gastos"),
];

/** Códigos ARES declarados por la entrada (para buscar por número). */
function codigosAres(route) {
  const claves =
    Array.isArray(route?.keys) && route.keys.length ? route.keys : [route?.programa ?? route?.key];
  return claves.filter((k) => /^ares\d+/i.test(String(k))).map((k) => String(k).toLowerCase());
}

/** Aplana el menú a una lista buscable de pantallas visibles para el rol. */
function construirIndice(routes, roleDetalles) {
  const items = [];
  let seccion = "";

  (routes || []).forEach((grupo) => {
    if (grupo?.type === "title") {
      seccion = grupo.title;
      return;
    }
    if (grupo?.type !== "collapse" || !grupo?.route || grupo.route === "/login") return;

    const hijos = (grupo.collapse || []).filter((c) => c?.type === "collapse" && c?.route);
    if (!hijos.length) {
      if (tieneAccesoEntrada(grupo, roleDetalles)) {
        items.push({
          nombre: grupo.name,
          ruta: grupo.route,
          grupo: grupo.adminZone ? "Administración" : seccion,
          codigos: codigosAres(grupo),
        });
      }
      return;
    }

    hijos
      .filter((hijo) => tieneAccesoEntrada(hijo, roleDetalles))
      .forEach((hijo) => {
        items.push({
          nombre: hijo.name,
          ruta: hijo.route,
          grupo: grupo.name,
          codigos: codigosAres(hijo),
        });
      });
  });

  return items;
}

function filtrar(indice, consulta) {
  const q = normalizar(consulta).trim();
  if (!q) return indice.slice(0, 10);
  const tokens = q.split(/\s+/);
  return indice
    .filter((item) => {
      const texto = normalizar(item.nombre + " " + item.grupo);
      return tokens.every(
        (t) => texto.includes(t) || item.codigos.some((c) => c.includes(t) || c === "ares" + t)
      );
    })
    .slice(0, 10);
}

function Subtitulo({ children }) {
  return (
    <Typography
      sx={{
        px: 2,
        pt: 1.2,
        pb: 0.3,
        fontSize: "0.66rem",
        fontWeight: 800,
        letterSpacing: "0.1em",
        color: "#94A3B8",
      }}
    >
      {children}
    </Typography>
  );
}

export default function BuscadorGlobal({ routes }) {
  const [controller] = useMaterialUIController();
  const { post } = useApi();
  const navigate = useNavigate();
  const [abierto, setAbierto] = useState(false);
  const [consulta, setConsulta] = useState("");
  const [seleccion, setSeleccion] = useState(0);
  const [clientes, setClientes] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [buscandoClientes, setBuscandoClientes] = useState(false);
  const inputRef = useRef(null);

  const puedeExpediente = useMemo(
    () => tienePermisosModulo(controller.roleDetalles, "expediente"),
    [controller.roleDetalles]
  );
  const puedeHistorial = useMemo(
    () => tienePermisosModulo(controller.roleDetalles, "historialchasis"),
    [controller.roleDetalles]
  );

  const indice = useMemo(() => {
    const pantallas = construirIndice(routes, controller.roleDetalles);
    const acciones = ACCIONES.filter((a) =>
      tienePermisosModulo(controller.roleDetalles, a.programa)
    );
    return [...pantallas, ...acciones];
  }, [routes, controller.roleDetalles]);
  const resultados = useMemo(() => filtrar(indice, consulta), [indice, consulta]);

  // Búsqueda de clientes con debounce: a partir de 3 caracteres.
  useEffect(() => {
    const q = consulta.trim();
    if (!abierto || !puedeExpediente || q.length < 3) {
      setClientes([]);
      setVehiculos([]);
      setBuscandoClientes(false);
      return undefined;
    }
    setBuscandoClientes(true);
    let activo = true;
    const timer = setTimeout(() => {
      post("/api/buscarclientes", { q })
        .then(({ ok, data }) => {
          if (!activo) return;
          setBuscandoClientes(false);
          setClientes(ok ? data.clientes || [] : []);
          setVehiculos(ok && puedeHistorial ? data.vehiculos || [] : []);
        })
        .catch(() => {
          if (!activo) return;
          setBuscandoClientes(false);
          setClientes([]);
          setVehiculos([]);
        });
    }, 300);
    return () => {
      activo = false;
      clearTimeout(timer);
    };
  }, [consulta, abierto, puedeExpediente, puedeHistorial, post]);

  // Lista combinada sobre la que se mueve la selección de teclado.
  const opciones = useMemo(
    () => [
      ...resultados.map((r) => ({ tipo: "pantalla", ...r })),
      // El backend trae `tipo` ("prestamo" | "contado"); aquí `tipo` es la
      // sección de la paleta, así que el del backend pasa a `tipoFila`.
      ...clientes.map((c) => ({ ...c, tipo: "cliente", tipoFila: c.tipo })),
      ...vehiculos.map((v) => ({ tipo: "vehiculo", ...v })),
    ],
    [resultados, clientes, vehiculos]
  );

  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && String(e.key).toLowerCase() === "k") {
        e.preventDefault();
        setAbierto(true);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    setSeleccion(0);
  }, [consulta, abierto]);

  const cerrar = () => {
    setAbierto(false);
    setConsulta("");
    setClientes([]);
    setVehiculos([]);
  };

  const irA = (item) => {
    if (!item) return;
    cerrar();
    if (
      item.tipo === "cliente" &&
      (item.tipoFila === "codeudor" || item.tipoFila === "registrado")
    ) {
      // Solo figura como co-deudor (tratamiento de co-deudor encendido) o
      // está registrado sin factura: no tiene préstamo propio, va a su
      // expediente por cédula.
      navigate(`/clientes/expediente/${encodeURIComponent(item.cedula)}`);
    } else if (item.tipo === "cliente" && item.tipoFila === "contado") {
      // Venta al contado: no hay préstamo al que saltar. Con cédula va al
      // expediente del cliente (vista 360 por persona); sin cédula lo único
      // que queda es el historial del chasis vendido.
      if (item.cedula) {
        navigate(`/clientes/expediente/${encodeURIComponent(item.cedula)}`);
      } else if (item.chasis) {
        navigate(`/inventario/historialchasis/${encodeURIComponent(item.chasis)}`);
      }
    } else if (item.tipo === "cliente") {
      navigate(`/prestamos/expediente/${item.noprest}`);
    } else if (item.tipo === "vehiculo") {
      navigate(`/inventario/historialchasis/${encodeURIComponent(item.chasis)}`);
    } else {
      navigate(item.ruta);
    }
  };

  const onDialogKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSeleccion((s) => Math.min(s + 1, opciones.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSeleccion((s) => Math.max(s - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      irA(opciones[seleccion]);
    }
  };

  const esMac = typeof navigator !== "undefined" && /Mac/i.test(navigator.platform || "");
  const hayClientesSeccion = puedeExpediente && consulta.trim().length >= 3;

  return (
    <>
      {/* Disparador para el sidebar */}
      <Box sx={{ px: 1.2, pt: 0.6 }}>
        <Box
          onClick={() => setAbierto(true)}
          role="button"
          aria-label="Buscar pantalla o cliente"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.2,
            py: 0.8,
            borderRadius: 1.4,
            cursor: "pointer",
            color: "#9AA8C2",
            bgcolor: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.08)",
            "&:hover": { bgcolor: "rgba(255,255,255,0.10)", color: "#FFFFFF" },
          }}
        >
          <SearchRoundedIcon sx={{ fontSize: 18 }} />
          <Typography sx={{ fontSize: "0.85rem", flex: 1 }}>Buscar…</Typography>
          <Typography
            sx={{
              fontSize: "0.68rem",
              px: 0.6,
              py: 0.1,
              borderRadius: 0.8,
              border: "1px solid rgba(255,255,255,0.18)",
            }}
          >
            {esMac ? "⌘K" : "Ctrl+K"}
          </Typography>
        </Box>
      </Box>

      <Dialog
        open={abierto}
        onClose={cerrar}
        onKeyDown={onDialogKeyDown}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: { borderRadius: 2.5 },
        }}
        sx={{ "& .MuiDialog-container": { alignItems: "flex-start", pt: "16vh" } }}
        TransitionProps={{ onEntered: () => inputRef.current?.focus() }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, px: 2, py: 1.4, borderBottom: "1px solid #E5E7EB" }}>
          <SearchRoundedIcon sx={{ color: "#94A3B8" }} />
          <InputBase
            inputRef={inputRef}
            autoFocus
            fullWidth
            placeholder={
              puedeExpediente
                ? "Pantalla, código, cliente o chasis (nombre, cédula, placa)…"
                : "Pantalla, módulo o código (ej. 4010, cuadre, morosidad)…"
            }
            value={consulta}
            onChange={(e) => setConsulta(e.target.value)}
            sx={{ fontSize: "1rem" }}
          />
          {buscandoClientes && <CircularProgress size={16} sx={{ color: "#94A3B8" }} />}
        </Box>
        <List sx={{ py: 0.5, maxHeight: 420, overflowY: "auto" }}>
          {resultados.length > 0 && hayClientesSeccion && <Subtitulo>PANTALLAS</Subtitulo>}
          {resultados.map((item, idx) => (
            <ListItemButton
              key={"p-" + item.ruta + item.nombre}
              selected={idx === seleccion}
              onMouseEnter={() => setSeleccion(idx)}
              onClick={() => irA(opciones[idx])}
              sx={{ px: 2, py: 1 }}
            >
              <ListItemText
                primary={item.nombre}
                secondary={item.grupo}
                primaryTypographyProps={{ fontSize: "0.95rem", fontWeight: 600 }}
                secondaryTypographyProps={{ fontSize: "0.78rem" }}
              />
            </ListItemButton>
          ))}

          {hayClientesSeccion && clientes.length > 0 && <Subtitulo>CLIENTES</Subtitulo>}
          {clientes.map((c, i) => {
            const idx = resultados.length + i;
            const contado = c.tipo === "contado";
            const codeudor = c.tipo === "codeudor";
            const registrado = c.tipo === "registrado";
            const estadoC = contado || codeudor || registrado ? null : estadoPrestamo(c.status, c.balance);
            const saldado = estadoC === "saldado";
            const incobrable = estadoC === "incobrable";
            const cancelado = estadoC === "cancelado" || incobrable || (contado && c.anulada);
            // Verde: préstamo saldado o venta al contado vigente. Gris: cancelado
            // o factura anulada. Azul: préstamo en curso.
            const verde = saldado || (contado && !c.anulada);
            let fondo = "#E8F0FE";
            let texto = "#1D4ED8";
            if (verde) {
              fondo = "#E8F5E9";
              texto = "#2E7D32";
            } else if (cancelado) {
              fondo = "#F1F5F9";
              texto = "#64748B";
            }
            let claveFila = `c-${c.noprest}`;
            if (codeudor) claveFila = `g-${c.cedula}`;
            else if (registrado) claveFila = `r-${c.cedula}`;
            else if (contado) claveFila = `f-${c.secuencia}`;
            let etiqueta;
            if (codeudor) {
              etiqueta = `Co-deudor · ${c.garantizados}`;
              fondo = "#FFF4E5";
              texto = "#B45309";
            } else if (registrado) {
              etiqueta = "Registrado";
              fondo = "#F1F5F9";
              texto = "#475569";
            } else if (contado) {
              etiqueta = `Contado F#${c.secuencia}`;
              if (c.anulada) etiqueta += " · anulada";
            } else if (saldado) {
              etiqueta = `#${c.noprest} · saldado`;
            } else if (incobrable) {
              etiqueta = `#${c.noprest} · incobrable`;
            } else if (cancelado) {
              etiqueta = `#${c.noprest} · cancelado`;
            } else {
              etiqueta = `#${c.noprest}`;
            }
            return (
              <ListItemButton
                key={claveFila}
                selected={idx === seleccion}
                onMouseEnter={() => setSeleccion(idx)}
                onClick={() => irA(opciones[idx])}
                sx={{ px: 2, py: 1 }}
              >
                <PersonRoundedIcon sx={{ fontSize: 20, color: "#94A3B8", mr: 1.4 }} />
                <ListItemText
                  primary={c.nombres}
                  secondary={[c.cedula, c.chasis].filter(Boolean).join(" · ") || "—"}
                  primaryTypographyProps={{ fontSize: "0.95rem", fontWeight: 600 }}
                  secondaryTypographyProps={{ fontSize: "0.78rem" }}
                />
                <Chip
                  label={etiqueta}
                  size="small"
                  sx={{
                    fontSize: "0.68rem",
                    height: 20,
                    bgcolor: fondo,
                    color: texto,
                  }}
                />
              </ListItemButton>
            );
          })}

          {hayClientesSeccion && vehiculos.length > 0 && <Subtitulo>CHASIS</Subtitulo>}
          {vehiculos.map((v, i) => {
            const idx = resultados.length + clientes.length + i;
            const vendido = String(v.estado || "").trim().toUpperCase() === "V";
            return (
              <ListItemButton
                key={"v-" + v.chasis}
                selected={idx === seleccion}
                onMouseEnter={() => setSeleccion(idx)}
                onClick={() => irA(opciones[idx])}
                sx={{ px: 2, py: 1 }}
              >
                <TwoWheelerRoundedIcon sx={{ fontSize: 20, color: "#94A3B8", mr: 1.4 }} />
                <ListItemText
                  primary={v.chasis}
                  secondary={[v.marca, v.modelo, v.colore, v.placa].filter(Boolean).join(" · ") || "—"}
                  primaryTypographyProps={{ fontSize: "0.95rem", fontWeight: 600 }}
                  secondaryTypographyProps={{ fontSize: "0.78rem" }}
                />
                <Chip
                  label={vendido ? "vendido" : "inventario"}
                  size="small"
                  sx={{
                    fontSize: "0.68rem",
                    height: 20,
                    bgcolor: vendido ? "#F1F5F9" : "#ECFDF5",
                    color: vendido ? "#64748B" : "#047857",
                  }}
                />
              </ListItemButton>
            );
          })}

          {opciones.length === 0 && !buscandoClientes && (
            <Typography sx={{ px: 2, py: 2.5, color: "#64748B", fontSize: "0.9rem" }}>
              Sin resultados para “{consulta}”.
              {puedeExpediente
                ? " Pruebe con el nombre de una pantalla, un código del sistema anterior, o el nombre/cédula/chasis de un cliente."
                : " Pruebe con el nombre de la pantalla o su código del sistema anterior."}
            </Typography>
          )}
        </List>
      </Dialog>
    </>
  );
}
